import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { ImageType } from 'react-images-uploading';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Button as MIButton } from '@material-ui/core';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { BlogUiModel } from '../../../shared/ui-models/blog.ui-model';
import { BLOG_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { birdToast } from '../../../shared/components/core-ui/toast';
import ImageUploader from '../../../shared/components/core-ui/image-uploader/image-uploader.component';
import { BLOG_IMAGE_ASPECT_RATIO } from '../../shared/config/blog-module.config';
import {
  addNewBlog,
  getBlog,
  updateBlog,
  uploadBlogImage
} from '../../shared/services/data.service';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { BlogPreviewModal } from './components/blog-preview-modal/blog-preview-modal.component';
import { EditBlogContent } from './components/edit-blog-content.container';
import { EditBlogContentArabic } from './components/edit-blog-content-ar.container';

type BlogTab = 'en' | 'ar';

export const EditBlogContainer = () => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const { t } = useTranslation();

  const isNew = !id;
  const [tab, setTab] = useState<BlogTab>('en');
  const [blog, setBlog] = useState<BlogUiModel | undefined>(isNew ? {} : undefined);
  const [isLoading, setIsLoading] = useState<boolean>(!isNew);
  const [image, setImage] = useState<ImageType | null>(null);
  const [pendingImage, setPendingImage] = useState<File | undefined>(undefined);
  const [draft, setDraft] = useState<BlogUiModel>({});
  const [showPreview, setShowPreview] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    getBlog(id).then((response: BlogUiModel | ErrorInfo) => {
      setIsLoading(false);
      if (isErrorInfo(response)) {
        birdToast.error(t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.LOAD_FAILED.MESSAGE'));
        return;
      }
      setBlog(response);
      setImage(response.image ? { dataURL: response.image } : null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const uploadImage = async (file: File, blogId: string) => {
    const uploaded = await uploadBlogImage(file, blogId);
    if (isErrorInfo(uploaded)) {
      throw uploaded;
    }
  };

  const handleCreate = (payload: BlogUiModel) => {
    if (!(payload.header && payload.headerAr && payload.body && payload.bodyAr && image)) {
      birdToast.info(t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.BOTH_LANGUAGES_REQUIRED.MESSAGE'));
      return;
    }

    const promise = addNewBlog(payload).then(async (response: BlogUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }

      const createdId = String(response.id);

      if (pendingImage) {
        await uploadImage(pendingImage, createdId).catch(() => {
          birdToast.error(t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_FAILED.MESSAGE'));
        });
      }

      setPendingImage(undefined);
      setDraft({});
      history.replace((BLOG_ROUTES.CHILDREN as RouteConfig).LIST.FULL_PATH);
    });

    birdToast.promise(promise, {
      loadingMessage: 'Creating blog...',
      successMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.CREATE_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.CREATE_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.CREATE_FAILED.MESSAGE')
    });
  };

  const handleValuesChange = (values: Partial<BlogUiModel>) => {
    setDraft((current) => ({ ...current, ...values }));
  };

  const handleSubmit = (updatedData: Partial<BlogUiModel>) => {
    if (isNew || !id) {
      handleCreate({ ...draft, ...updatedData });
      return;
    }

    const promise = updateBlog(id, { ...draft, ...updatedData }).then(
      (response: BlogUiModel | ErrorInfo) => {
        if (isErrorInfo(response)) {
          throw response;
        }
        setDraft({});
        setBlog(response);
      }
    );

    birdToast.promise(promise, {
      loadingMessage: 'Updating blog...',
      successMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.UPDATE_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.UPDATE_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.UPDATE_FAILED.MESSAGE')
    });
  };

  const handleImageUpload = (uploadedImage: ImageType) => {
    if (!(uploadedImage && uploadedImage.file)) return;

    setImage(uploadedImage);

    if (!id) {
      setPendingImage(uploadedImage.file);
      birdToast.info(t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_PENDING.MESSAGE'));
      return;
    }

    const promise = uploadImage(uploadedImage.file, id);

    birdToast.promise(promise, {
      loadingMessage: 'Uploading image...',
      successMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_FAILED.MESSAGE')
    });
  };

  const tabClassName = (tabKey: BlogTab) =>
    `cursor-pointer border-b-2 px-[1.5rem] py-[0.8rem] text-[1.6rem] font-bold ${
      tab === tabKey ? 'border-primary-500 text-primary-500' : 'border-transparent text-gray-500'
    }`;

  return (
    <div className="flex h-full flex-col items-start justify-start md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="flex w-full items-center justify-between gap-[1rem]">
        <div className="flex items-center gap-[0.5rem]">
          <MIButton
            aria-label={t('BLOG_MODULE.EDIT_CONTAINER.BACK')}
            onClick={() => {
              history.push((BLOG_ROUTES.CHILDREN as RouteConfig).LIST.FULL_PATH);
            }}
          >
            <ArrowBackIcon fontSize="large" color="inherit" />
          </MIButton>
          <div className="my-[1rem] text-[2.5rem] font-bold text-primary-500">
            {isNew
              ? t('BLOG_MODULE.EDIT_CONTAINER.NEW_TITLE')
              : t('BLOG_MODULE.EDIT_CONTAINER.EDIT_TITLE')}
          </div>
        </div>

        <Button type={ButtonType.primaryInverted} onClick={() => setShowPreview(true)}>
          {t('BLOG_MODULE.EDIT_CONTAINER.PREVIEW')}
        </Button>
      </div>

      <div className="mb-[2rem] flex w-full flex-col gap-[0.5rem] rounded-[4px] bg-white p-[2rem] shadow-[0_2px_4px_0_rgba(187,186,186,0.5)]">
        <div className="font-medium">{t('BLOG_MODULE.EDIT_CONTAINER.IMAGE')}</div>
        <ImageUploader
          data={{ image, imageUrl: image?.dataURL }}
          config={{ enableCrop: true, cropAspectRatio: BLOG_IMAGE_ASPECT_RATIO }}
          eventHandlers={{
            fileUploadHandler: handleImageUpload,
            imageRemovedHandler: () => {
              setImage(null);
            }
          }}
        />
      </div>

      <div className="mb-[2rem] flex w-full items-center gap-[1rem] border-b border-gray-200">
        <div className={tabClassName('en')} onClick={() => setTab('en')}>
          {t('BLOG_MODULE.EDIT_CONTAINER.TAB_EN')}
        </div>
        <div className={tabClassName('ar')} onClick={() => setTab('ar')}>
          {t('BLOG_MODULE.EDIT_CONTAINER.TAB_AR')}
        </div>
      </div>

      <div className="w-full rounded-[4px] bg-white p-[2rem] shadow-[0_2px_4px_0_rgba(187,186,186,0.5)]">
        <div className={tab === 'en' ? 'block' : 'hidden'}>
          <EditBlogContent
            data={{ blog }}
            config={{ isLoading, isNew }}
            eventHandlers={{
              submitHandler: handleSubmit,
              valuesChangeHandler: handleValuesChange
            }}
          />
        </div>
        <div className={tab === 'ar' ? 'block' : 'hidden'}>
          <EditBlogContentArabic
            data={{ blog }}
            config={{ isLoading, isNew }}
            eventHandlers={{
              submitHandler: handleSubmit,
              valuesChangeHandler: handleValuesChange
            }}
          />
        </div>
      </div>

      <BlogPreviewModal
        config={{ showModal: showPreview }}
        data={{ blog: { ...blog, ...draft }, imageUrl: image?.dataURL }}
        eventHandlers={{ closeHandler: () => setShowPreview(false) }}
      />
    </div>
  );
};

export default EditBlogContainer;
